import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Stack,
  FormControlLabel,
  FormGroup,
  IconButton,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import CopyAllIcon from '@mui/icons-material/CopyAll';
import './FloorDrawing.scss';
import * as fabric from 'fabric';
import UploadIcon from '@mui/icons-material/Upload';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Crop54Icon from '@mui/icons-material/Crop54';
import { createToastNotification } from '../../utilities/helpers';
import { fetchAllHalls, fetchHallByID } from '../../api/hall';
import { Hall } from '../../common/interfaces/hall';
import { FloorCard } from './FloorCard';
import { createFloorPlan, updateFloorPlan } from '../../api/fairs';
import ConnectedTvIcon from '@mui/icons-material/ConnectedTv';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DrawIcon from '@mui/icons-material/Draw';
import { v4 as uuidv4 } from 'uuid';
import {
  BARRIER,
  createPolygon,
  createStandWithStandNumber,
  createTemporaryLine,
  createTv,
  getModifiedStandObjectWithStandNumber,
  createRectangle,
  DEVICE,
  ObjectData,
  STAND,
  TVTEXT,
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  FONT_SIZE,
  SelectedObjectI,
} from './util/ObjectTypes';
import { importExhibitors } from '../../api/exhibitors';
import FenceIcon from '@mui/icons-material/Fence';
import ConfirmationModal from '../../components/ConfirmationModal';
import { fetchFloorPlan } from '../../api/floor-plans';
import ViewListIcon from '@mui/icons-material/ViewList';
import { ExhibitorListModal } from './modal/ExhibitorListModal';
import LayersIcon from '@mui/icons-material/Layers';
import { FloorPlanBaseListModal } from './modal/FloorPlanBaseListModal';
import { fetchFloorPlanBase } from '../../api/floor-plan-base';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { SwitchButton } from '../../components/SwitchButton/SwitchButton';

const FloorDrawing: FC<{
  floorPlan?: any;
  fairID: number;
  fairName: string;
  handleClose: () => void;
  setFloorPlan: (floorPlan: any) => void;
  fetchFairDetails: () => Promise<void>;
}> = ({
  fairID,
  floorPlan,
  handleClose,
  fairName,
  fetchFairDetails,
  setFloorPlan,
}) => {
  // canvas
  const [isCanvasPopulated, setIsCanvasPopulated] = useState<boolean>(false);
  const objectMetaDataRef = useRef<{ [key: string]: ObjectData }>({});
  const [isFloorPlanBaseUploaded, setIsFloorPlanBaseUploaded] =
    useState<boolean>(false);
  const [canvas, setCanvas] = useState<fabric.Canvas | null>(null);
  const [barrierCreated, setBarrierCreated] = useState<string>('');
  const [objectMoved, setObjectMoved] = useState<string>('');
  const [selectedObject, setSelectedObject] = useState<SelectedObjectI | null>(
    null
  );
  const [points, setPoints] = useState([]);
  const [drawing, setDrawing] = useState({
    color: 'primary',
    isDrawing: false,
  });
  const [freeHandDrawingColor, setFreeHandDrawingColor] = useState('primary');

  const MIN_ZOOM = 0.2;
  const MAX_ZOOM = 8;

  const zoomLevel = useRef<number>(1);
  const startX = useRef<number>(1);
  const startY = useRef<number>(1);
  const isDragging = useRef<boolean>(false);

  //blueprint
  const [blueprint, setBlueprint] = useState<string | null>(null);
  const [blueprintWidth, setBlueprintWidth] = useState(50); // % of viewport width
  const [blueprintHeight, setBlueprintHeight] = useState(50); // % of viewport height
  const [blueprintIsResizing, setBlueprintIsResizing] = useState(false);
  const [blueprintStartX, setBlueprintStartX] = useState(0);
  const [blueprintStartY, setBlueprintStartY] = useState(0);
  const [blueprintPosX, setBlueprintPosX] = useState(10); // % of viewport width (left position)
  const [blueprintPosY, setBlueprintPosY] = useState(10); // % of viewport height (top position)
  const [blueprintIsMoving, setBlueprintIsMoving] = useState(false);
  const [blueprintIsShown, setBlueprintIsShown] = useState<boolean>(true);
  const [blueprintIsLocked, setBlueprintIsLocked] = useState<boolean>(false);

  // floor plan
  const [floorPlanName, setFloorPlanName] = useState('');
  const [floorPlanIsLocked, setFloorPlanIsLocked] = useState<boolean>(false);
  const [floorPlanBarriersIsLocked, setFloorPlanBarriersIsLocked] =
    useState<boolean>(true);

  // floor card
  const [isFloorCardVisible, setIsFloorCardVisible] = useState<boolean>(false);

  // halls
  const [halls, setHalls] = useState<Hall[]>([]);
  const [selectedHall, setSelectedHall] = useState<Hall | null>(null);
  const hallsPerPage = 10;
  const [hallsPage, setHallsPage] = useState<number>(1);
  const [allHallFetched, setAllHallFetched] = useState(false);

  // exit confirmation modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // should duplicate object
  const [shouldDuplicatedNow, setShouldDuplicatedNow] = useState('');

  // should standardization stand number
  const [
    shouldStandardizationStandNumber,
    setShouldStandardizationStandNumber,
  ] = useState('');

  // exhibitor list modal
  const [isExhibitorListModalOpen, setIsExhibitorListModalOpen] =
    useState(false);

  // floor plan base list modal
  const [isFloorPlanBaseListModalOpen, setIsFloorPlanBaseListModalOpen] =
    useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    let initCanvas = new fabric.Canvas('floor-canvas', {
      width: CANVAS_WIDTH,
      height: CANVAS_HEIGHT,
    });

    if (!isCanvasPopulated) {
      if (floorPlan) {
        populateCanvas(initCanvas, floorPlan);
      } else {
        initCanvas = addZoomEventToCanvas(initCanvas);
        initCanvas = addMouseDownEventToCanvas(initCanvas);
        initCanvas = addMouseMoveEventToCanvas(initCanvas);
        initCanvas = addMouseUpEventToCanvas(initCanvas);
        addKeyboardEventForDuplication();

        setCanvas(initCanvas);
        initCanvas.on('selection:cleared', () => {
          setSelectedObject(null);
        });
        initCanvas.on('path:created', function (event) {
          handleFreeHandBarrierCreated(event.path);
        });
        initCanvas.on('object:modified', function (event) {
          setObjectMoved(new Date().getTime() + '');
        });
      }
      setIsCanvasPopulated(true);
    }

    return () => {
      initCanvas.dispose();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (barrierCreated || objectMoved) {
      postponeAutoSave();
    }
  }, [barrierCreated, objectMoved]);

  useEffect(() => {
    duplicateStand();
  }, [shouldDuplicatedNow]);

  useEffect(() => {
    if (!canvas) return;
    canvas.getObjects('line').forEach((obj) => {
      //@ts-ignore
      if (obj.isTemporary) {
        canvas.remove(obj);
      }
    });

    if (!drawing.isDrawing) return;
    for (let i = 1; i < points.length; i++) {
      const line = createTemporaryLine(points, i);
      canvas.add(line);
    }
  }, [canvas, points, drawing]);

  ////
  //// Functions
  ////

  const postponeAutoSave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(performAutoSave, 10000);
  };

  const performAutoSave = () => {
    handleSaveFloorPlan(true);
  };

  const handleMouseDown = (event: any) => {
    if (!canvas || !drawing.isDrawing) return;

    // Get pointer position
    const pointer = canvas?.getPointer(event);
    const newPoint = { x: pointer.x, y: pointer.y };

    //@ts-ignore
    setPoints((prevPoints) => [...prevPoints, newPoint]);
  };

  const handleDoubleClick = () => {
    if (!canvas || !drawing.isDrawing || points.length < 3) return;

    const id = STAND + '-' + uuidv4();
    const polygon = createPolygon(points);
    polygon.set('id', id);

    // @ts-ignore
    canvas.add(polygon);
    polygon.on('mousedblclick', () => handleObjectClick(polygon));

    objectMetaDataRef.current = {
      ...objectMetaDataRef.current,
      [id]: {
        type: STAND,
        ids: [],
        attachMouseDownEvent: true,
      },
    };

    // Reset the state for the next drawing
    setPoints([]);
    setDrawing({ color: 'primary', isDrawing: false });
    canvas.getObjects('line').forEach((obj) => {
      //@ts-ignore
      if (obj.isPreview) {
        canvas.remove(obj);
      }
    });
    postponeAutoSave();
  };

  const handleMouseMove = (event: any) => {
    if (blueprintIsResizing) {
      const deltaX = event.clientX - blueprintStartX;
      const deltaY = event.clientY - blueprintStartY;

      setBlueprintWidth((prev) =>
        Math.min(Math.max(prev + deltaX * 0.05, 10), 100)
      );
      setBlueprintHeight((prev) =>
        Math.min(Math.max(prev + deltaY * 0.05, 10), 100)
      );

      setBlueprintStartX(event.clientX);
      setBlueprintStartY(event.clientY);
    } else if (blueprintIsMoving) {
      const deltaX = event.clientX - blueprintStartX;
      const deltaY = event.clientY - blueprintStartY;

      setBlueprintPosX((prev) =>
        Math.min(Math.max(prev + deltaX * 0.05, 0), 100 - blueprintWidth)
      );
      setBlueprintPosY((prev) =>
        Math.min(Math.max(prev + deltaY * 0.05, 0), 100 - blueprintHeight)
      );

      setBlueprintStartX(event.clientX);
      setBlueprintStartY(event.clientY);
    } else {
      if (!canvas || !drawing.isDrawing) return;
      const pointer = canvas.getPointer(event);
      const lastPoint = points[points.length - 1];
      if (!lastPoint) return;

      canvas.getObjects('line').forEach((obj) => {
        //@ts-ignore
        if (obj.isPreview) {
          canvas.remove(obj);
        }
      });

      const previewLine = new fabric.Line(
        //@ts-ignore
        [lastPoint.x, lastPoint.y, pointer.x, pointer.y],
        {
          stroke: 'gray',
          strokeWidth: 1,
          selectable: false,
          isPreview: true, // Mark this line as a preview
        }
      );
      canvas.add(previewLine);
      canvas.renderAll();
    }
  };

  const centerObjectsOnCanvas = (canvas: fabric.Canvas) => {
    if (!canvas) return;
    const objects = canvas.getObjects();
    if (objects.length === 0) return;

    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();

    const boundingRect = objects.reduce(
      (acc, obj) => {
        return {
          left: Math.min(acc.left, obj.left || 0),
          top: Math.min(acc.top, obj.top || 0),
          right: Math.max(
            acc.right,
            (obj.left || 0) + (obj.width || 0) * (obj.scaleX || 1)
          ),
          bottom: Math.max(
            acc.bottom,
            (obj.top || 0) + (obj.height || 0) * (obj.scaleY || 1)
          ),
        };
      },
      { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity }
    );

    const contentWidth = boundingRect.right - boundingRect.left;
    const contentHeight = boundingRect.bottom - boundingRect.top;

    const contentCenterX = boundingRect.left + contentWidth / 2;
    const contentCenterY = boundingRect.top + contentHeight / 2;

    const canvasCenterX = canvasWidth / 2;
    const canvasCenterY = canvasHeight / 2;

    const offsetX = canvasCenterX - contentCenterX;
    const offsetY = canvasCenterY - contentCenterY;

    objects.forEach((object) => {
      object.set({
        left: object.left + offsetX,
        top: object.top + offsetY,
      });
    });
  };

  const populateCanvas = async (initCanvas: fabric.Canvas, floorPlan: any) => {
    try {
      if (!floorPlan) {
        return () => {
          initCanvas.dispose();
        };
      }

      setFloorPlanName(floorPlan.name);
      const canvasJsonFileResponse = await fetch(floorPlan.data);
      const jsonBlob = await canvasJsonFileResponse.blob();
      let canvasJsonFile = new File([jsonBlob], 'floor-plan.txt', {
        type: 'application/json',
      });

      const reader = new FileReader();
      let canvasData: any = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          if (reader.result !== null) {
            resolve(reader.result as string);
          } else {
            reject(new Error('FileReader result is null'));
          }
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsText(canvasJsonFile);
      });
      canvasData = JSON.parse(canvasData);

      if (canvasData.canvas && canvasData.items) {
        const svgObjects = await fabric.loadSVGFromString(canvasData.canvas);
        let standObjects = [];
        let televisionObjects = [];
        for (let i = 0; i < svgObjects.objects.length; i++) {
          //@ts-ignore
          const object = svgObjects.objects[i];

          //@ts-ignore
          if (object.id && object.id.startsWith(DEVICE)) {
            //@ts-ignore
            if (object.text === TVTEXT) {
              continue;
            }
            //@ts-ignore
            televisionObjects.push(object);

            //@ts-ignore
          } else if (object.id && object.id.startsWith(STAND)) {
            standObjects.push(object);
          } else {
            object?.set({
              lockMovementX: true,
              lockMovementY: true,
              lockScalingX: true,
              lockScalingY: true,
              lockRotation: true,
              selectable: false,
              lockVisibility: true,
              evented: false,
              visible: true,
            });
            // @ts-ignore
            initCanvas.add(object);
          }
        }

        // @ts-ignore
        let standObjectsMap = standObjects.reduce(function (acc, object) {
          // @ts-ignore
          if (!acc[object.id]) {
            // @ts-ignore
            acc[object.id] = [];
          }
          // @ts-ignore
          acc[object.id].push(object);
          return acc;
        }, {});

        // @ts-ignore
        for (const [id, object] of Object.entries(standObjectsMap)) {
          let rectIndex = 0;
          let textIndex = 0;
          // @ts-ignore
          if (object[textIndex].text) {
            rectIndex = 1;
          } else {
            textIndex = 1;
          }

          //@ts-ignore
          const group = createStandWithStandNumber(
            {
              // @ts-ignore
              width: object[rectIndex].width,
              // @ts-ignore
              height: object[rectIndex].height,
              // @ts-ignore
              left: object[rectIndex].left,
              // @ts-ignore
              top: object[rectIndex].top,
              // @ts-ignore
              scaleX: object[rectIndex].scaleX,
              // @ts-ignore
              scaleY: object[rectIndex].scaleY,
              // @ts-ignore
              points: object[rectIndex].points ?? null,
              // @ts-ignore
              path: object[rectIndex].path ?? null,
              // @ts-ignore
              pathOffSet: object[rectIndex].pathOffSet ?? null,
              // @ts-ignore
              angle: object[rectIndex].angle ?? 0,
              // @ts-ignore
            },
            id,
            // @ts-ignore
            object[textIndex]?.text ?? ''
          );

          updateTextInGroup(group, FONT_SIZE);
          //@ts-ignore
          group.set('id', id);
          group.on('mousedblclick', () => handleObjectClick(group));
          initCanvas.add(group);
        }

        // @ts-ignore
        let televisionObjectsMap = televisionObjects.reduce(function (
          acc,
          object
        ) {
          // @ts-ignore
          if (!acc[object.id]) {
            // @ts-ignore
            acc[object.id] = [];
          }
          // @ts-ignore
          acc[object.id].push(object);
          return acc;
        }, {});

        // @ts-ignore
        for (const [id, object] of Object.entries(televisionObjectsMap)) {
          // @ts-ignore
          const television = createTv(object[0].left, object[0].top);
          television.set('id', id);
          television.on('mousedblclick', () => handleObjectClick(television));
          initCanvas.add(television);
        }
        centerObjectsOnCanvas(initCanvas);
        objectMetaDataRef.current = canvasData.items;
        initCanvas = addZoomEventToCanvas(initCanvas);
        initCanvas = addMouseDownEventToCanvas(initCanvas);
        initCanvas = addMouseMoveEventToCanvas(initCanvas);
        initCanvas = addMouseUpEventToCanvas(initCanvas);
        addKeyboardEventForDuplication();
        initCanvas.on('path:created', function (event) {
          handleFreeHandBarrierCreated(event.path);
        });
        initCanvas.on('object:modified', function (event) {
          setShouldStandardizationStandNumber(new Date().getTime() + '');
          setObjectMoved(new Date().getTime() + '');
        });
        setCanvas(initCanvas);
        setIsFloorPlanBaseUploaded(true);
        initCanvas.renderAll();

        if (canvasData.blueprint?.content) {
          setBlueprint(canvasData.blueprint.content);
          setBlueprintWidth(canvasData.blueprint.width);
          setBlueprintHeight(canvasData.blueprint.height);
          setBlueprintStartX(canvasData.blueprint.startX);
          setBlueprintStartY(canvasData.blueprint.startY);
          setBlueprintPosX(canvasData.blueprint.posX);
          setBlueprintPosY(canvasData.blueprint.posY);
        }

        return () => {
          initCanvas.dispose();
        };
      }

      createToastNotification(
        'error',
        'Prostorni plan je nevalidan, molimo Vas kontaktirajte programere.'
      );
    } catch (error) {
      createToastNotification(
        'error',
        'Dogodila se greška prilikom učitavanja prostornog plana, molimo Vas kontaktirajte programere.'
      );
    }

    return () => {
      initCanvas.dispose();
    };
  };

  const updateTextInGroup = (activeObject: any, newFontSize: number) => {
    if (activeObject.id.startsWith(DEVICE)) return;

    if (activeObject && activeObject.type === 'group') {
      const groupCenterX =
        activeObject.left + (activeObject.width * activeObject.scaleX) / 2;
      const groupCenterY =
        activeObject.top + (activeObject.height * activeObject.scaleY) / 2;
      activeObject.forEachObject((obj: any) => {
        if (obj.type === 'text') {
          const newText = new fabric.Text(obj.text, {
            left: groupCenterX,
            top: groupCenterY,
            fontSize: newFontSize,
            fill: 'black',
            hasBorders: false,
            fontWeight: 'bold',
            fontFamily: 'Arial',
            originX: 'center',
            originY: 'center',
          });
          activeObject.remove(obj);
          activeObject.add(newText);
        }
      });
    }
  };

  useEffect(() => {
    if (canvas?.getActiveObject()) {
      updateTextInGroup(canvas?.getActiveObject(), FONT_SIZE);
    }
  }, [shouldStandardizationStandNumber]);

  const initialFetchHalls = async () => {
    try {
      const response = await fetchAllHalls({ page: 1, perPage: hallsPerPage });
      let halls = response.halls;
      if (floorPlan?.hall?.id) {
        let found = false;
        for (let i = 0; i < response.halls.length; i++) {
          // eslint-disable-next-line
          if (response.halls[i].id == floorPlan.hall.id) {
            found = true;
            setSelectedHall(response.halls[i]);
            break;
          }
        }

        if (!found) {
          try {
            const hallResponse = await fetchHallByID(floorPlan.hall.id);
            setSelectedHall(hallResponse);
            halls = [...halls, hallResponse];
          } catch (error: any) {
            createToastNotification('error', error.response?.data?.message);
          }
        }
      }

      setHalls(halls);
    } catch (error: any) {
      createToastNotification('error', error.response?.data?.message);
    }
  };

  const loadMoreHalls = async () => {
    try {
      const response = await fetchAllHalls({
        page: hallsPage + 1,
        perPage: hallsPerPage,
      });
      if (!response.itemCount) {
        setAllHallFetched(true);
      }
      setHallsPage(hallsPage + 1);
      const map = new Map();
      [...halls, ...response.halls].forEach((item) => map.set(item.id, item));
      setHalls(Array.from(map.values()));
    } catch (error: any) {
      createToastNotification('error', error.response?.data?.message);
    }
  };

  const removeSelectedObject = async () => {
    if (drawing.isDrawing || canvas?.isDrawingMode) {
      let text = drawing.isDrawing ? 'štandova' : 'granica';
      createToastNotification(
        'warning',
        'Ne možete uklanjati objekte, u toku je crtanje ' + text
      );
      return;
    }

    if (canvas) {
      const activeObject = canvas.getActiveObject();
      if (activeObject) {
        const objectId = (activeObject as unknown as { id?: string }).id;
        if (objectId) {
          let objectsMetaData = objectMetaDataRef.current;
          // @ts-ignore
          if (objectsMetaData[objectId]) {
            // @ts-ignore
            delete objectsMetaData[objectId];
          }
          objectMetaDataRef.current = objectsMetaData;
        }

        canvas.remove(activeObject);
        setSelectedObject(null);
        performAutoSave();
      } else {
        createToastNotification('error', 'Nijedan element nije izabran!');
      }
    }
  };

  const getCorners = (obj: fabric.Object) => {
    if (!obj?.left || !obj?.top || !obj?.width || !obj?.height) return [];
    const { left, top, width, height, scaleX = 1, scaleY = 1 } = obj;
    const scaledWidth = width * scaleX;
    const scaledHeight = height * scaleY;

    return [
      { x: left, y: top },
      { x: left + scaledWidth, y: top },
      { x: left, y: top + scaledHeight },
      { x: left + scaledWidth, y: top + scaledHeight },
    ];
  };

  const SNAP_DISTANCE = 20;
  const snapActiveObject = (canvas: fabric.Canvas) => {
    if (!canvas) {
      return;
    }
    const allObjects = canvas.getObjects();
    const activeObject = canvas.getActiveObject();
    // @ts-ignore
    const standObjects = allObjects.filter(
      // @ts-ignore
      (obj) => obj.id?.startsWith(STAND) && obj !== activeObject
    );
    // @ts-ignore
    const activeCorners = getCorners(activeObject);

    const getDistance = (
      p1: { x: number; y: number },
      p2: { x: number; y: number }
    ) => {
      return Math.sqrt(Math.pow(p1.x - p2.x, 2) + Math.pow(p1.y - p2.y, 2));
    };

    const nearbyStands = standObjects.filter((stand) => {
      const standCorners = getCorners(stand);
      return activeCorners.some((activeCorner) =>
        standCorners.some(
          (standCorner) =>
            getDistance(activeCorner, standCorner) < SNAP_DISTANCE
        )
      );
    });

    if (nearbyStands.length === 0) return;

    let closestStand = null;
    let minDistance = Infinity;
    let closestActiveCorner = null;
    let closestStandCorner = null;

    for (let i = 0; i < nearbyStands.length; i++) {
      const stand = nearbyStands[i];
      const standCorners = getCorners(stand);
      for (let j = 0; j < activeCorners.length; j++) {
        const activeCorner = activeCorners[j];
        for (let k = 0; k < standCorners.length; k++) {
          const standCorner = standCorners[k];
          const distance = getDistance(activeCorner, standCorner);
          if (distance < minDistance) {
            minDistance = distance;
            closestStand = stand;
            closestActiveCorner = activeCorner;
            closestStandCorner = standCorner;
          }
        }
      }
    }
    if (!closestStand || !closestActiveCorner || !closestStandCorner) return;

    activeObject?.set({
      left: activeObject?.left + (closestStandCorner.x - closestActiveCorner.x),
      top: activeObject?.top + (closestStandCorner.y - closestActiveCorner.y),
    });
  };

  const zoomIn = () => {
    if (canvas) {
      let zoom = canvas.getZoom();
      const center = new fabric.Point(canvas.width! / 2, canvas.height! / 2);
      zoom *= 1.1;
      zoom = Math.min(zoom, MAX_ZOOM);
      canvas.zoomToPoint(center, zoom);
    }
  };

  const zoomOut = () => {
    if (canvas) {
      let zoom = canvas.getZoom();
      const center = new fabric.Point(canvas.width! / 2, canvas.height! / 2);
      zoom /= 1.1;
      zoom = Math.max(zoom, MIN_ZOOM);
      canvas.zoomToPoint(center, zoom);
    }
  };

  const addZoomEventToCanvas = (canvas: fabric.Canvas) => {
    canvas.setZoom(1);
    canvas.wrapperEl.addEventListener('wheel', (event: any) => {
      event.preventDefault();
      if (event.ctrlKey) {
        const delta = event.deltaY;
        zoomLevel.current = canvas.getZoom();
        const zoomFactor = 1.05;
        if (delta < 0) {
          zoomLevel.current *= zoomFactor;
        } else if (delta > 0) {
          zoomLevel.current /= zoomFactor;
        }
        zoomLevel.current = Math.max(
          MIN_ZOOM,
          Math.min(MAX_ZOOM, zoomLevel.current)
        );
        canvas.zoomToPoint(canvas.getPointer(event), zoomLevel.current);
      }
    });
    return canvas;
  };

  const addMouseDownEventToCanvas = (canvas: fabric.Canvas) => {
    canvas.wrapperEl.addEventListener('mousedown', (event) => {
      if (event.shiftKey && event.button === 0) {
        isDragging.current = true;
        startX.current = event.clientX;
        startY.current = event.clientY;
      }
    });
    return canvas;
  };

  const addKeyboardEventForDuplication = () => {
    window.addEventListener('keydown', (event) => {
      if (event.ctrlKey && event.key.toLowerCase() == 'q') {
        setShouldDuplicatedNow(new Date().getTime() + '');
      }
    });
  };

  const addMouseMoveEventToCanvas = (canvas: fabric.Canvas) => {
    canvas.wrapperEl.addEventListener('mousemove', (event) => {
      if (isDragging.current && event.shiftKey && event.button === 0) {
        const dx = event.clientX - startX.current;
        const dy = event.clientY - startY.current;
        const zoom = canvas.getZoom();
        // @ts-ignore
        canvas.relativePan({ x: dx / zoom, y: dy / zoom });
        startX.current = event.clientX;
        startY.current = event.clientY;
      }
    });
    return canvas;
  };

  const addMouseUpEventToCanvas = (canvas: fabric.Canvas) => {
    canvas.wrapperEl.addEventListener('mouseup', (event) => {
      if (event.ctrlKey) {
        // @ts-ignore
        snapActiveObject(canvas);
      }

      isDragging.current = false;
    });
    return canvas;
  };

  useEffect(() => {
    initialFetchHalls();
    // eslint-disable-next-line
  }, []);

  const addRectangle = () => {
    if (drawing.isDrawing || canvas?.isDrawingMode) {
      let text = drawing.isDrawing ? 'štandova' : 'granica';
      createToastNotification(
        'warning',
        'Ne možete dodati štand, u toku je crtanje ' + text
      );
      return;
    }

    if (canvas) {
      const id = STAND + `-${uuidv4()}`;
      const rect = createRectangle(canvas, id);
      rect.on('mousedblclick', () => handleObjectClick(rect));
      canvas.add(rect);
      objectMetaDataRef.current = {
        ...objectMetaDataRef.current,
        [id]: {
          type: STAND,
          ids: [],
          attachMouseDownEvent: true,
        },
      };
      postponeAutoSave();
    }
  };

  const duplicateStand = () => {
    if (!canvas) return;

    const activeObject = canvas.getActiveObject();
    if (!activeObject) {
      createToastNotification(
        'info',
        'Morate prvo izabrati štand za dupliranje'
      );
      return;
    }

    const objectId = (activeObject as unknown as { id?: string }).id;
    if (!objectId) return;

    const objectsMetaData = objectMetaDataRef.current;
    if (!objectsMetaData[objectId] || objectsMetaData[objectId].type !== STAND)
      return;

    const newId = STAND + `-${uuidv4()}`;
    let newStand: any;

    // @ts-ignore
    if (activeObject.points) {
      newStand = createStandWithStandNumber(
        {
          // @ts-ignore
          points: activeObject.points.map((p: any) => ({
            x: p.x + 20,
            y: p.y + 20,
          })),
          left: activeObject.left + 20,
          top: activeObject.top + 20,
          scaleX: activeObject.scaleX,
          scaleY: activeObject.scaleY,
          angle: activeObject.angle,
        },
        newId,
        ''
      );
      // @ts-ignore
      newStand.on('mousedblclick', () => handleObjectClick(newStand));
    } else {
      newStand = createStandWithStandNumber(
        {
          left: activeObject.left + 20,
          top: activeObject.top + 20,
          width: activeObject.width,
          height: activeObject.height,
          scaleX: activeObject.scaleX,
          scaleY: activeObject.scaleY,
          angle: activeObject.angle,
        },
        newId,
        ''
      );
      // @ts-ignore
      newStand.on('mousedblclick', () => handleObjectClick(newStand));
    }

    if (newStand) {
      canvas.add(newStand);
      objectMetaDataRef.current = {
        ...objectMetaDataRef.current,
        [newId]: {
          type: STAND,
          ids: [],
          attachMouseDownEvent: true,
        },
      };
      canvas.renderAll();
      postponeAutoSave();
    }
  };

  const addVerticalTelevision = () => {
    if (drawing.isDrawing || canvas?.isDrawingMode) {
      let text = drawing.isDrawing ? 'štandova' : 'granica';
      createToastNotification(
        'warning',
        'Ne možete dodati televizor, u toku je crtanje ' + text
      );
      return;
    }

    if (canvas) {
      const television = createTv(
        canvas.getWidth() / 2,
        canvas.getHeight() / 2
      );
      const tvID = `${DEVICE}-${uuidv4()}`;
      television.set('id', tvID);
      objectMetaDataRef.current = {
        ...objectMetaDataRef.current,
        [tvID]: {
          type: DEVICE,
          ids: [],
          attachMouseDownEvent: true,
        },
      };

      television.on('mousedblclick', () => handleObjectClick(television));
      canvas.add(television);
      postponeAutoSave();
    }
  };

  const handleObjectClick = (object: any) => {
    // @ts-ignore
    if (objectMetaDataRef.current && objectMetaDataRef.current[object.id]) {
      setSelectedObject({
        canvasObjectID: object.id,
        metadata: objectMetaDataRef.current[object.id],
      });
      setIsFloorCardVisible(true);
    }
  };

  const handleSaveObjectMetaData = (
    selectedObject: SelectedObjectI,
    objectDatabaseIDs: number[],
    standNumber?: string
  ) => {
    if (selectedObject) {
      let obj = objectMetaDataRef.current[selectedObject.canvasObjectID];

      // @ts-ignore
      obj.ids = objectDatabaseIDs;
      delete objectMetaDataRef.current[selectedObject.canvasObjectID];
      objectMetaDataRef.current = {
        ...objectMetaDataRef.current,
        [selectedObject.canvasObjectID]: obj,
      };

      if (
        canvas &&
        selectedObject.canvasObjectID.includes(STAND) &&
        standNumber
      ) {
        let newStand = getModifiedStandObjectWithStandNumber(
          canvas,
          selectedObject.canvasObjectID,
          standNumber
        );
        newStand.on('mousedblclick', () => {
          handleObjectClick(newStand);
        });
        updateTextInGroup(newStand, FONT_SIZE);
        canvas.add(newStand);
      }

      performAutoSave();
      setIsFloorCardVisible(false);
    }
  };

  const removeExhibitorFromObject = (
    eID: number,
    selectedObject: SelectedObjectI
  ) => {
    if (selectedObject) {
      let standNumber: string | null = null;
      let standObjectData =
        objectMetaDataRef.current[selectedObject.canvasObjectID];

      // @ts-ignore
      const updatedIds = standObjectData.ids?.filter((num) => num !== eID);
      standObjectData.ids = updatedIds ?? [];
      delete objectMetaDataRef.current[selectedObject.canvasObjectID];
      objectMetaDataRef.current = {
        ...objectMetaDataRef.current,
        [selectedObject.canvasObjectID]: standObjectData,
      };

      // @ts-ignore
      if (updatedIds?.length <= 0) {
        standNumber = '';
      }

      let objectOnCanvas = null;
      let canvasObjects = canvas?.getObjects();
      // @ts-ignore
      for (let i = 0; i < canvasObjects?.length; i++) {
        // @ts-ignore
        if (canvasObjects[i].id === selectedObject.canvasObjectID) {
          // @ts-ignore
          if (standNumber !== '') {
            let textIndex = 0;
            // @ts-ignore
            if (!canvasObjects[i]._objects[textIndex].text) {
              textIndex = 1;
            }

            // @ts-ignore
            standNumber = canvasObjects[i]._objects[textIndex].text;
            continue;
          }

          // @ts-ignore
          objectOnCanvas = canvasObjects[i];
          if (standNumber !== null) {
            break;
          }
        }
      }

      if (standNumber === null) {
        standNumber = '';
      }

      if (objectOnCanvas) {
        //@ts-ignore
        const objectId = objectOnCanvas.id;
        const objectData = {
          // @ts-ignore
          width: objectOnCanvas.width1 ?? objectOnCanvas.width,
          // @ts-ignore
          height: objectOnCanvas.height1 ?? objectOnCanvas.height,
          // @ts-ignore
          left: objectOnCanvas.left1 ?? objectOnCanvas.left,
          // @ts-ignore
          top: objectOnCanvas.top1 ?? objectOnCanvas.top,
          // @ts-ignore
          angle: objectOnCanvas._objects[0].angle ?? 0,
          scaleX: objectOnCanvas.scaleX,
          scaleY: objectOnCanvas.scaleY,
          //@ts-ignore
          points: objectOnCanvas.points ?? null,
        };

        const group = createStandWithStandNumber(
          objectData,
          objectId,
          standNumber
        );
        group.set('id', objectId);
        group.on('mousedblclick', () => handleObjectClick(group));
        canvas?.remove(objectOnCanvas);
        canvas?.add(group);
      }

      performAutoSave();
    }
  };

  const removeAllExhibitorsFromObject = (selectedObject: SelectedObjectI) => {
    let activeObjectOnCanvas = canvas?.getActiveObject();
    if (
      selectedObject &&
      activeObjectOnCanvas &&
      //@ts-ignore
      selectedObject.canvasObjectID === activeObjectOnCanvas.id
    ) {
      //@ts-ignore
      const objectId = activeObjectOnCanvas.id;
      const objectData = {
        //@ts-ignore
        width: activeObjectOnCanvas.width1 ?? activeObjectOnCanvas.width,
        //@ts-ignore
        height: activeObjectOnCanvas.height1 ?? activeObjectOnCanvas.height,
        //@ts-ignore
        left: activeObjectOnCanvas.left1 ?? activeObjectOnCanvas.left,
        //@ts-ignore
        top: activeObjectOnCanvas.top1 ?? activeObjectOnCanvas.top,
        //@ts-ignore
        angle: activeObjectOnCanvas._objects[0].angle ?? 0,
        scaleX: activeObjectOnCanvas.scaleX,
        scaleY: activeObjectOnCanvas.scaleY,
        //@ts-ignore
        points: activeObjectOnCanvas.points ?? null,
      };

      let standObjectData =
        objectMetaDataRef.current[selectedObject.canvasObjectID];
      standObjectData.ids = [];
      objectMetaDataRef.current = {
        ...objectMetaDataRef.current,
        [selectedObject.canvasObjectID]: standObjectData,
      };

      const group = createStandWithStandNumber(objectData, objectId, '');
      group.set('id', objectId);
      group.on('mousedblclick', () => handleObjectClick(group));
      canvas?.remove(activeObjectOnCanvas);
      canvas?.add(group);

      performAutoSave();
    }
  };

  const detachExhibitorFromStand = (exhibitorID: number) => {
    let selectedObject: SelectedObjectI | null = null;
    for (const id in objectMetaDataRef.current) {
      //@ts-ignore
      if (objectMetaDataRef.current[id].ids?.includes(exhibitorID)) {
        selectedObject = {
          canvasObjectID: id,
          metadata: objectMetaDataRef.current[id],
        };
        break;
      }
    }

    if (selectedObject) {
      removeExhibitorFromObject(exhibitorID, selectedObject);
    }
  };

  const handleFreeHandBarrierCreated = (pathObject: fabric.Object) => {
    const barrierID = `${BARRIER}-${uuidv4()}`;

    pathObject.set('id', barrierID);

    setBarrierCreated(new Date().getTime() + '');
  };

  const handleFreeHandDrawing = () => {
    if (drawing.isDrawing) {
      createToastNotification(
        'warning',
        'Ne možete crtati granice, u toku je crtanje štandova'
      );
      return;
    }

    if (canvas) {
      canvas.isDrawingMode = !canvas.isDrawingMode;
      if (!canvas.freeDrawingBrush) {
        canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
      }
      canvas.freeDrawingBrush.width = 2;
      canvas.freeDrawingBrush.color = 'black';

      let color = 'primary';
      if (canvas.isDrawingMode) {
        color = 'red';
      }
      setFreeHandDrawingColor(color);
    }
  };

  const handleSaveFloorPlan = async (isAutoSave = false) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!selectedHall) {
      if (isAutoSave) {
        return;
      }
      createToastNotification('info', 'Odaberite halu!');
      return;
    }

    if (!floorPlanName.trim()) {
      if (isAutoSave) {
        return;
      }
      createToastNotification('info', 'Naziv prostornog plana je obavezan!');
      return;
    }

    if (!canvas) {
      createToastNotification(
        'error',
        'Osnova prostornog plana nije postavljena!'
      );
      return;
    }

    const viewTransform = canvas.viewportTransform;
    canvas.setViewportTransform([0, 0, 0, 0, 0, 0]);
    const canvasSvg = canvas.toSVG();
    // @ts-ignore
    canvas.setViewportTransform(viewTransform);

    let blueprintData = null;
    if (
      blueprint &&
      blueprintWidth !== null &&
      blueprintHeight !== null &&
      blueprintStartX !== null &&
      blueprintStartY !== null &&
      blueprintPosX !== null &&
      blueprintPosY !== null
    ) {
      blueprintData = {
        content: blueprint,
        width: blueprintWidth,
        height: blueprintHeight,
        startX: blueprintStartX,
        startY: blueprintStartY,
        posX: blueprintPosX,
        posY: blueprintPosY,
      };
    }

    const jsonData = {
      canvas: canvasSvg,
      items: objectMetaDataRef.current,
      blueprint: blueprintData,
    };

    // @ts-ignore
    const jsonFile = new Blob([JSON.stringify(jsonData)], {
      type: 'application/json',
    });

    let exhibitors: number[] = [];
    // @ts-ignore
    Object.keys(objectMetaDataRef.current).forEach((key) => {
      if (
        objectMetaDataRef.current[key].type === STAND &&
        objectMetaDataRef.current[key].ids?.length
      ) {
        // @ts-ignore
        exhibitors = [...exhibitors, ...objectMetaDataRef.current[key].ids];
      }
    });

    let devices: number[] = [];
    // @ts-ignore
    Object.keys(objectMetaDataRef.current).forEach((key) => {
      if (
        objectMetaDataRef.current[key].type === DEVICE &&
        objectMetaDataRef.current[key].ids?.length &&
        //@ts-ignore
        objectMetaDataRef.current[key].ids[0]
      ) {
        //@ts-ignore
        devices = [...devices, Number(objectMetaDataRef.current[key].ids[0])];
      }
    });

    let plan = {
      name: floorPlanName,
      data: new File([jsonFile], floorPlanName + '.txt', {
        type: 'application/json',
      }),
      exhibitors: exhibitors,
      devices: devices,
      fairID: fairID,
      hallID: selectedHall.id,
    };

    try {
      let savedPlan;
      if (floorPlan?.id) {
        await updateFloorPlan(plan, floorPlan.id);
      } else {
        savedPlan = await createFloorPlan(plan);
      }

      if (isAutoSave && savedPlan?.ID) {
        let response = await fetchFloorPlan(savedPlan.ID);
        setFloorPlan(response.floorPlan);
        return response.floorPlan;
      } else if (!isAutoSave) {
        createToastNotification('success', 'Prostorni plan uspešno sačuvan');
        handleClose();
        fetchFairDetails();
      }
    } catch (error: any) {
      createToastNotification('error', error?.response?.data?.message);
    }
  };

  const lockObjects = () => {
    if (canvas) {
      const objects = canvas.getObjects();
      objects.forEach((object) => {
        object.set({
          lockMovementX: true,
          lockMovementY: true,
          lockScalingX: true,
          lockScalingY: true,
          lockRotation: true,
          lockVisibility: true,
          selectable: false,
          evented: false,
        });
      });
      setFloorPlanIsLocked(true);
      canvas.renderAll();
      createToastNotification('success', 'Štandovi i uređaji su zaključani');
    }
  };

  const unlockObject = () => {
    canvas?.getObjects().forEach((object) => {
      if (
        //@ts-ignore
        object.id?.startsWith(STAND) ||
        //@ts-ignore
        object.id?.startsWith(DEVICE)
      ) {
        object.set({
          lockMovementX: false,
          lockMovementY: false,
          lockScalingX: false,
          lockScalingY: false,
          lockRotation: false,
          lockVisibility: false,
          selectable: true,
          evented: true,
        });
      }
    });

    setFloorPlanIsLocked(false);
    createToastNotification('success', 'Štandovi i uređaji su otključani');
    canvas?.renderAll();
  };

  const unlockBarriers = () => {
    canvas?.getObjects().forEach((object) => {
      if (
        //@ts-ignore
        object.id?.startsWith(BARRIER)
      ) {
        object.set({
          lockMovementX: false,
          lockMovementY: false,
          lockScalingX: false,
          lockScalingY: false,
          lockRotation: false,
          lockVisibility: false,
          selectable: true,
          evented: true,
        });
      }
    });

    setFloorPlanBarriersIsLocked(false);
    createToastNotification('success', 'Granice su otključane');
    canvas?.renderAll();
  };

  const lockBarriers = () => {
    if (canvas) {
      const objects = canvas.getObjects();
      objects.forEach((object) => {
        if (
          //@ts-ignore
          object.id?.startsWith(BARRIER)
        ) {
          object.set({
            lockMovementX: true,
            lockMovementY: true,
            lockScalingX: true,
            lockScalingY: true,
            lockRotation: true,
            lockVisibility: true,
            selectable: false,
            evented: false,
          });
        }
      });

      setFloorPlanBarriersIsLocked(true);
      canvas.renderAll();
      createToastNotification('success', 'Granice su zaključane');
    }
  };

  const loadSVGObjectsIntoCanvas = async (canvasSvg: string) => {
    const svg = await fabric.loadSVGFromString(canvasSvg);

    let barriers = [];

    const objects = svg.objects;

    // @ts-ignore
    let objects2 = [];
    for (let i = 0; i < objects.length; i++) {
      // @ts-ignore
      if (objects[i].id && objects[i].id.startsWith('Layer_1')) {
        // @ts-ignore
        objects[i].id = `${BARRIER}-INIT-${uuidv4()};`;
        barriers.push(objects[i]);

        continue;
      }

      // @ts-ignore
      if (objects[i].id && objects[i].id.startsWith('_')) {
        // @ts-ignore
        objects[i].id = STAND + objects[i].id;
        objectMetaDataRef.current = {
          ...objectMetaDataRef.current,
          // @ts-ignore
          [objects[i].id]: {
            type: STAND,
            ids: [],
            attachMouseDownEvent: true,
          },
        };
      }

      if (objects[i]?.type === 'text') {
        continue;
      }

      // @ts-ignore
      objects[i].set({
        lockMovementX: true,
        lockMovementY: true,
        lockScalingX: true,
        lockScalingY: true,
        lockRotation: true,
        lockVisibility: true,
        selectable: false,
        evented: false,
      });

      objects2.push(objects[i]);
    }

    // @ts-ignore
    const group = new fabric.Group(objects2, {
      lockMovementX: true,
      lockMovementY: true,
      lockScalingX: true,
      lockScalingY: true,
      lockRotation: true,
      lockVisibility: true,
      selectable: false,
      evented: false,
    });

    //@ts-ignore
    // group.scaleToWidth(canvas?.width * 0.8);
    // @ts-ignore
    // group.scaleToHeight(canvas?.height * 0.8);

    canvas?.add(group);
    for (let i = 0; i < barriers.length; i++) {
      // @ts-ignore
      barriers[i].set({
        lockMovementX: true,
        lockMovementY: true,
        lockScalingX: true,
        lockScalingY: true,
        lockRotation: true,
        lockVisibility: true,
        selectable: false,
        evented: false,
      });
      // @ts-ignore
      canvas?.add(barriers[i]);
    }

    setCanvas(canvas);
    setIsFloorPlanBaseUploaded(true);
    canvas?.renderAll();
  };

  const handleImport = async (
    floorPlanID: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];

    if (file) {
      if (
        !['csv', 'xls', 'xlsx'].includes(
          file.name.split('.').pop()?.toLowerCase() || ''
        )
      ) {
        alert('Dozvoljeni su CSV, XLS i XLSX fajlovi.');
        return;
      }
      try {
        let exhibitors = await importExhibitors(floorPlanID, file);
        if (canvas) {
          //@ts-ignore
          const groupedExhibitors = exhibitors.exhibitors.reduce(
            // @ts-ignore
            (acc, exhibitor) => {
              let boothNumber = exhibitor.boothNumber;

              if (!acc[boothNumber]) {
                acc[boothNumber] = [];
              }

              acc[boothNumber].push(exhibitor);

              return acc;
            },
            {} as Record<number, any[]>
          );

          let svgObjects = canvas.getObjects();
          for (let i = 0; i < svgObjects.length; i++) {
            //@ts-ignore
            if (svgObjects[i].id && svgObjects[i].id.startsWith(STAND)) {
              //@ts-ignore
              let bootNumber = svgObjects[i].id.split('stand_')[1];
              let exhibitors = groupedExhibitors[bootNumber];
              if (!exhibitors) {
                continue;
              }
              let standObject = {
                //@ts-ignore
                canvasObjectID: svgObjects[i].id,
              };

              //@ts-ignore
              handleSaveObjectMetaData(
                // @ts-ignore
                standObject,
                // @ts-ignore
                exhibitors.map((e) => e.id),
                bootNumber
              );
            }
          }
        }

        createToastNotification('success', 'Uspešan uvoz izlagača');
      } catch (error: any) {
        console.log(error);
        createToastNotification('error', error?.response?.data?.message);
      }
    }
  };

  const [filters, setFilters] = useState({
    stand: true,
    tv: true,
    barrier: true,
  });

  // @ts-ignore
  const handleFilterChange = (event) => {
    const updatedFilters = {
      ...filters,
      [event.target.name]: event.target.checked,
    };

    setFilters(updatedFilters);

    const objects = canvas?.getObjects();

    objects?.forEach((object) => {
      // @ts-ignore
      if (object.id) {
        // @ts-ignore
        if (object.id.startsWith(STAND) && !updatedFilters.stand) {
          object.set('visible', false);
        }
        // @ts-ignore
        else if (object.id.startsWith(DEVICE) && !updatedFilters.tv) {
          object.set('visible', false);
        }
        // @ts-ignore
        else if (object.id.startsWith(BARRIER) && !updatedFilters.barrier) {
          object.set('visible', false);
        } else {
          object.set('visible', true);
        }
      }
    });

    canvas?.renderAll();
  };

  const handleStandBlueprintUpload = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      if (
        !['jpg', 'jpeg', 'png'].includes(
          file.name.split('.').pop()?.toLowerCase() as string
        ) ||
        !['image/jpg', 'image/jpeg', 'image/png'].includes(
          file.type.split('.').pop()?.toLowerCase() as string
        )
      ) {
        alert('Dozvoljeni su PNG, JPG i JPEG fajlovi.');
        return;
      }

      try {
        const reader = new FileReader();
        reader.onloadend = () => {
          setBlueprint(reader.result as string);
          createToastNotification('success', 'Uspešno priložen nacrt');
        };
        reader.readAsDataURL(file);
      } catch (error: any) {
        createToastNotification('error', 'Nacrt nije postavljen uspešno');
      }
    }
  };

  const handleBlueprintMouseDownResize = (event: React.MouseEvent) => {
    if (!event.altKey || blueprintIsLocked) return;
    event.stopPropagation();
    setBlueprintIsResizing(true);
    setBlueprintStartX(event.clientX);
    setBlueprintStartY(event.clientY);
  };

  const handleBlueprintMouseDownMove = (event: React.MouseEvent) => {
    if (!event.altKey || blueprintIsLocked) return;
    event.stopPropagation();
    setBlueprintIsMoving(true);
    setBlueprintStartX(event.clientX);
    setBlueprintStartY(event.clientY);
  };

  const handleMouseUp = () => {
    setBlueprintIsResizing(false);
    setBlueprintIsMoving(false);
  };

  return (
    <Box display="flex" flexDirection="column" p={2}>
      <Stack
        direction="row"
        spacing={3}
        alignItems="center"
        justifyContent="space-between"
        ml={2}
        className="floor-drawing__header"
      >
        <Typography variant="h6">{fairName}</Typography>
        <TextField
          id="outlined-basic"
          label="Naziv prostornog plana *"
          variant="outlined"
          sx={{ width: '50%' }}
          value={floorPlanName}
          onChange={(e) => {
            setFloorPlanName(e.target.value);
          }}
        />
        <Autocomplete
          sx={{ width: '20%' }}
          options={[...halls, { id: 'load-more', name: 'Učitaj još hala' }]}
          getOptionLabel={(option) => option.name}
          value={selectedHall}
          onChange={(event, newValue) => {
            if (newValue?.id !== 'load-more') {
              // @ts-ignore
              setSelectedHall(newValue);
            }
          }}
          renderOption={(props, option) =>
            option.id === 'load-more' ? (
              <li {...props} key="load-more">
                <Box sx={{ width: '100%', textAlign: 'center' }}>
                  <Button
                    disabled={allHallFetched}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(event) => {
                      event.stopPropagation();
                      loadMoreHalls();
                    }}
                    fullWidth
                  >
                    {allHallFetched ? 'Učitane su sve hale' : 'Učitaj još hala'}
                  </Button>
                </Box>
              </li>
            ) : (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )
          }
          renderInput={(params) => (
            <TextField {...params} label="Izaberi halu *" variant="outlined" />
          )}
        />
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" onClick={openModal}>
            Otkaži
          </Button>
          <Button
            variant="contained"
            onClick={() => handleSaveFloorPlan(false)}
          >
            Sačuvaj
          </Button>
        </Stack>
      </Stack>
      {floorPlan?.id && (
        <Box
          sx={{
            position: 'absolute',
            top: 160,
            right: 16,
            zIndex: 10,
            width: '200px',
            padding: '8px',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            border: '1px solid lightgray',
            borderRadius: '8px',
            backgroundColor: '#f9f9f9',
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Prikaži
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <SwitchButton
                  checked={filters.stand}
                  onChange={handleFilterChange}
                  name="stand"
                  sx={{ m: 1 }}
                />
              }
              label="Štand"
            />
            <FormControlLabel
              control={
                <SwitchButton
                  checked={filters.tv}
                  onChange={handleFilterChange}
                  name="tv"
                  sx={{ m: 1 }}
                />
              }
              label="TV"
            />
            <FormControlLabel
              control={
                <SwitchButton
                  checked={filters.barrier}
                  onChange={handleFilterChange}
                  name="barrier"
                  sx={{ m: 1 }}
                />
              }
              label="Barijera"
            />
            {blueprint && (
              <FormControlLabel
                control={
                  <SwitchButton
                    checked={blueprintIsShown}
                    onChange={() => {
                      setBlueprintIsShown((prev) => !prev);
                    }}
                    sx={{ m: 1 }}
                  />
                }
                label="Nacrt"
              />
            )}
          </FormGroup>
        </Box>
      )}
      {floorPlan?.id && (
        <Box
          sx={{
            position: 'absolute',
            top: 390,
            right: 16,
            zIndex: 10,
            width: '200px',
            padding: '8px',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            border: '1px solid lightgray',
            borderRadius: '8px',
            backgroundColor: '#f9f9f9',
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            Zaključaj
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <SwitchButton
                  checked={floorPlanIsLocked}
                  onChange={() => {
                    if (floorPlanIsLocked) {
                      unlockObject();
                    } else {
                      lockObjects();
                    }
                  }}
                  sx={{ m: 1 }}
                />
              }
              label="Prostorni plan"
            />
            <FormControlLabel
              control={
                <SwitchButton
                  checked={floorPlanBarriersIsLocked}
                  onChange={() => {
                    if (floorPlanBarriersIsLocked) {
                      unlockBarriers();
                    } else {
                      lockBarriers();
                    }
                  }}
                  sx={{ m: 1 }}
                />
              }
              label="Granice"
            />
            {blueprint && (
              <FormControlLabel
                control={
                  <SwitchButton
                    checked={blueprintIsLocked}
                    onChange={() => {
                      setBlueprintIsLocked((prev) => !prev);
                    }}
                    sx={{ m: 1 }}
                  />
                }
                label="Nacrt"
              />
            )}
          </FormGroup>
        </Box>
      )}
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        mt={2}
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <canvas
          style={{
            pointerEvents: 'auto',
            cursor: 'pointer',
            border: 'solid #eef0f2 2px',
            borderRadius: 20,
          }}
          id="floor-canvas"
        />
        {blueprint && blueprintIsShown && (
          <>
            <img
              id="blueprint"
              src={blueprint}
              alt=""
              onClick={handleBlueprintMouseDownMove}
              style={{
                position: 'fixed',
                top: `${Math.max(5, blueprintPosY)}vh`,
                left: `${Math.max(5, blueprintPosX)}vw`,
                width: `${Math.min(90, blueprintWidth)}vw`,
                height: `${Math.min(90, blueprintHeight)}vh`,
                objectFit: 'contain',
                opacity: 0.5,
                zIndex: 9999,
                cursor: blueprintIsResizing ? 'nwse-resize' : 'move',
                userSelect: 'none',
                pointerEvents: blueprintIsLocked ? 'none' : 'auto',
              }}
            />
            {/* Resize Handle (small box at bottom-right corner) */}
            <div
              onMouseDown={handleBlueprintMouseDownResize}
              style={{
                position: 'fixed',
                top: `${blueprintPosY + blueprintHeight - 2}vh`,
                left: `${blueprintPosX + blueprintWidth - 2}vw`,
                width: '10px',
                height: '10px',
                background: 'red',
                zIndex: 10000,
                cursor: 'nwse-resize',
                pointerEvents: blueprintIsLocked ? 'none' : 'auto',
              }}
            />
          </>
        )}
      </Box>
      {isFloorPlanBaseUploaded && (
        <Stack alignItems="center" justifyContent="center">
          {floorPlan?.id && (
            <Stack
              alignItems="left"
              sx={{
                position: 'absolute',
                top: 160,
                left: 16,
                zIndex: 10,
                width: '250px',
              }}
            >
              <Stack direction="row" justifyContent="center" spacing={1} mt={1}>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={<MapOutlinedIcon />}
                  sx={{ width: '100%' }}
                >
                  Postavi nacrt standova
                  <input
                    type="file"
                    hidden
                    accept=".png, .jpg, .jpeg"
                    onChange={(e) => {
                      handleStandBlueprintUpload(e);
                    }}
                  />
                </Button>
                {blueprint && (
                  <Tooltip title="Ukloni nacrt štandova" placement="top">
                    <IconButton
                      color="error"
                      onClick={(event) => {
                        setBlueprint(null);
                      }}
                    >
                      <DeleteOutlineIcon color="error" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>

              <Button
                variant="contained"
                component="label"
                startIcon={<ViewListIcon />}
                onClick={() => {
                  setIsExhibitorListModalOpen(true);
                }}
                sx={{
                  marginTop: 2,
                }}
              >
                Pregled izlagača
              </Button>
              <Button
                variant="contained"
                component="label"
                startIcon={<UploadIcon />}
                sx={{
                  marginTop: 2,
                }}
              >
                Uvezi izlagače
                <input
                  type="file"
                  hidden
                  accept=".csv, .xls, .xlsx"
                  onChange={(e) => {
                    handleImport(floorPlan.id, e);
                  }}
                />
              </Button>
            </Stack>
          )}
          <Stack
            direction="row"
            justifyContent="center"
            spacing={3}
            mt={2}
            width="70%"
            sx={{
              border: '1px solid lightgray',
              padding: '8px',
              borderRadius: '8px',
              backgroundColor: '#f9f9f9',
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Tooltip title="Iscrtaj štand" placement="top">
              <Button
                sx={{ backgroundColor: drawing.color }}
                variant="contained"
                onClick={() => {
                  if (canvas?.isDrawingMode) {
                    createToastNotification(
                      'warning',
                      'Ne možete crtati štandove, u toku je crtanje granica'
                    );
                    return;
                  }

                  if (drawing.isDrawing) {
                    setPoints([]);
                    setDrawing({ color: 'primary', isDrawing: false });
                    canvas?.getObjects('line').forEach((obj) => {
                      //@ts-ignore
                      if (obj.isPreview) {
                        canvas?.remove(obj);
                      }
                    });
                  } else {
                    setPoints([]);
                    setDrawing({ isDrawing: true, color: 'red' });
                  }
                }}
              >
                <DrawIcon />
              </Button>
            </Tooltip>

            <Tooltip title="Postavi štand" placement="top">
              <Button
                variant="contained"
                color="primary"
                onClick={addRectangle}
              >
                <Crop54Icon />
              </Button>
            </Tooltip>
            <Tooltip title="Postavi TV" placement="top">
              <Button
                variant="contained"
                color="primary"
                onClick={addVerticalTelevision}
              >
                <ConnectedTvIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Iscrtaj granice" placement="top">
              <Button
                variant="contained"
                sx={{ backgroundColor: freeHandDrawingColor }}
                onClick={() => {
                  handleFreeHandDrawing();
                }}
              >
                <FenceIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Obrisi element" placement="top">
              <Button
                variant="contained"
                color="error"
                onClick={removeSelectedObject}
              >
                <DeleteOutlineIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Zumiraj" placement="top">
              <Button variant="outlined" onClick={zoomIn}>
                <ZoomInIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Odzumiraj" placement="top">
              <Button variant="outlined" onClick={zoomOut}>
                <ZoomOutIcon />
              </Button>
            </Tooltip>
            <Tooltip title="Dupliraj štand" placement="top">
              <Button
                variant="contained"
                color="primary"
                onClick={duplicateStand}
              >
                <CopyAllIcon />
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
      )}
      {!isFloorPlanBaseUploaded && (
        <Stack
          direction="column"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          position="absolute"
          top="50%"
          left="50%"
          sx={{
            transform: 'translate(-50%, -50%)',
          }}
        >
          <Button
            variant="contained"
            component="label"
            startIcon={<LayersIcon />}
            disabled={!selectedHall || !floorPlanName.trim()}
            onClick={() => {
              setIsFloorPlanBaseListModalOpen(true);
            }}
          >
            Odaberi osnovu prostornog plana
          </Button>
          {(!selectedHall || !floorPlanName.trim()) && (
            <Typography variant="body2" color="error">
              Neophodno je prvo uneti naziv prostornog plana i odabrati halu
            </Typography>
          )}
        </Stack>
      )}
      {
        <>
          <FloorCard
            fairID={fairID}
            floorPlanID={floorPlan?.id}
            isVisible={isFloorCardVisible}
            setIsVisible={setIsFloorCardVisible}
            selectedObject={selectedObject}
            handleSaveObjectMetaData={handleSaveObjectMetaData}
            setSelectedObject={setSelectedObject}
            removeAllExhibitorsFromObject={removeAllExhibitorsFromObject}
            canvas={canvas}
            updateTextInGroup={updateTextInGroup}
          />
          <ExhibitorListModal
            fairID={fairID}
            floorPlanID={floorPlan?.id}
            isVisible={isExhibitorListModalOpen}
            setIsVisible={setIsExhibitorListModalOpen}
            removeExhibitorFromObject={detachExhibitorFromStand}
          />
          <FloorPlanBaseListModal
            isVisible={isFloorPlanBaseListModalOpen}
            setIsVisible={setIsFloorPlanBaseListModalOpen}
            setFloorPlanBase={async (baseID: number) => {
              try {
                const response = await fetchFloorPlanBase(baseID);

                const file = await fetch(response.source);
                await loadSVGObjectsIntoCanvas(await file.text());
                if (canvas) {
                  centerObjectsOnCanvas(canvas);
                }

                let fp = await handleSaveFloorPlan(true);

                await canvas?.dispose();

                await populateCanvas(
                  new fabric.Canvas('floor-canvas', {
                    width: CANVAS_WIDTH,
                    height: CANVAS_HEIGHT,
                  }),
                  fp
                );
              } catch (error: any) {
                createToastNotification(
                  'error',
                  error?.response?.data?.message
                );
              }
            }}
          />
        </>
      }
      {isModalOpen && (
        <ConfirmationModal
          confirmationModalTitle="Potvrda otkazivanja"
          modalDescription="Da li ste sigurni da želite da otkažete izmenu prostornog plana?"
          confirmBtnTitle="Da"
          cancelBtnTitle="Ne"
          cancelAction={closeModal}
          handleAction={() => {
            handleClose();
            closeModal();
            fetchFairDetails();
          }}
        />
      )}
    </Box>
  );
};

export default FloorDrawing;
